/**
 * Animation Utilities for Mobile
 * 
 * Provides performance-optimized animations for mobile devices
 * with fallbacks for desktop browsers.
 */

const MOBILE_THRESHOLD = 768;

/**
 * Check if current device is mobile based on viewport width
 */
export function isMobileDevice() {
  return window.innerWidth <= MOBILE_THRESHOLD;
}

/**
 * Apply a fade-in animation to an element
 * @param {Element} element - DOM element to animate
 * @param {Object} options - Animation options
 */
export function fadeIn(element, options = {}) {
  const defaults = {
    duration: 300,
    delay: 0,
    easing: 'ease-in',
    onComplete: null
  };
  
  const config = { ...defaults, ...options };
  
  if (!element) return;
  
  // Different approach for mobile vs desktop for performance
  if (isMobileDevice()) {
    // Simpler animation for mobile
    element.style.transition = `opacity ${config.duration}ms ${config.easing} ${config.delay}ms`;
    element.style.opacity = '0';
    
    // Force reflow
    void element.offsetWidth;
    
    // Start animation
    element.style.opacity = '1';
    
    // Handle callback
    if (config.onComplete) {
      setTimeout(config.onComplete, config.duration + config.delay);
    }
  } else {
    // Use Web Animation API for desktop
    const keyframes = [
      { opacity: 0 },
      { opacity: 1 }
    ];
    
    const animationOptions = {
      duration: config.duration,
      delay: config.delay,
      easing: config.easing,
      fill: 'forwards'
    };
    
    const animation = element.animate(keyframes, animationOptions);
    
    if (config.onComplete) {
      animation.onfinish = config.onComplete;
    }
  }
}

/**
 * Apply a slide-in animation from bottom
 * @param {Element} element - DOM element to animate
 * @param {Object} options - Animation options
 */
export function slideInFromBottom(element, options = {}) {
  const defaults = {
    duration: 400,
    delay: 0,
    distance: '30px',
    easing: 'cubic-bezier(0.17, 0.84, 0.44, 1)',
    onComplete: null
  };
  
  const config = { ...defaults, ...options };
  
  if (!element) return;

  if (isMobileDevice()) {
    // Simpler animation for mobile
    element.style.transform = `translateY(${config.distance})`;
    element.style.opacity = '0';
    element.style.transition = `
      transform ${config.duration}ms ${config.easing} ${config.delay}ms,
      opacity ${config.duration * 0.8}ms ${config.easing} ${config.delay}ms
    `;
    
    // Force reflow
    void element.offsetWidth;
    
    // Start animation
    element.style.transform = 'translateY(0)';
    element.style.opacity = '1';
    
    if (config.onComplete) {
      setTimeout(config.onComplete, config.duration + config.delay);
    }
  } else {
    // Use Web Animation API for desktop
    const keyframes = [
      { transform: `translateY(${config.distance})`, opacity: 0 },
      { transform: 'translateY(0)', opacity: 1 }
    ];
    
    const animationOptions = {
      duration: config.duration,
      delay: config.delay,
      easing: config.easing,
      fill: 'forwards'
    };
    
    const animation = element.animate(keyframes, animationOptions);
    
    if (config.onComplete) {
      animation.onfinish = config.onComplete;
    }
  }
}

/**
 * Staggered animation for list items
 * @param {NodeList|Array} elements - DOM elements to animate
 * @param {Function} animationFunc - Animation function to apply
 * @param {Object} options - Animation options
 */
export function staggeredAnimation(elements, animationFunc, options = {}) {
  const defaults = {
    staggerDelay: 50,
    initialDelay: 0,
    reverse: false
  };
  
  const config = { ...defaults, ...options };
  
  if (!elements || !elements.length || !animationFunc) return;
  
  // Convert NodeList to Array if needed
  const elementsArray = Array.from(elements);
  
  // Reverse order if specified
  if (config.reverse) {
    elementsArray.reverse();
  }
  
  // Apply animation with staggered delay
  elementsArray.forEach((element, index) => {
    const delay = config.initialDelay + (index * config.staggerDelay);
    animationFunc(element, { ...options, delay });
  });
}

/**
 * Optimize sidebar transitions for better performance
 * @param {Element} sidebarElement - The sidebar DOM element to optimize
 */
export function optimizeSidebarTransition(sidebarElement) {
  if (!sidebarElement) return;
  
  // Apply performance optimizations
  sidebarElement.style.willChange = 'transform, width';
  sidebarElement.style.transform = 'translateZ(0)';
  sidebarElement.style.backfaceVisibility = 'hidden';
  
  // Ensure smooth transitions
  const currentTransition = sidebarElement.style.transition;
  if (!currentTransition.includes('cubic-bezier')) {
    sidebarElement.style.transition = 
      `width 0.3s cubic-bezier(0.25, 0.1, 0.25, 1), 
       transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)`;
  }
  
  // Optimize children elements that might affect rendering
  const children = sidebarElement.querySelectorAll('.contact-info, .icon-box');
  children.forEach(child => {
    child.style.willChange = 'transform, opacity';
    child.style.transform = 'translateZ(0)';
    child.style.backfaceVisibility = 'hidden';
  });
}

export default {
  isMobileDevice,
  fadeIn,
  slideInFromBottom,
  staggeredAnimation,
  optimizeSidebarTransition
};
